/**
 * Organizations Database References
 *
 * Organizations and related collections:
 * - /clients/{clientKey}/organizations
 * - /clients/{clientKey}/organization_memberships
 * - /clients/{clientKey}/organization_invitations
 */

import {
  doc,
  collection,
  query,
  where,
  orderBy,
  limit,
  DocumentReference,
  CollectionReference,
  Query,
  DocumentData
} from 'firebase/firestore';
import { db } from '../../firebase';

/* ═══════════════════════════════════════════════════════════
   ORGANIZATIONS COLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to the organizations collection
 * Path: /clients/{clientKey}/organizations
 */
export const OrganizationsRef_Collection = (
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'organizations');
};

/**
 * Reference to a specific organization document
 * Path: /clients/{clientKey}/organizations/{orgId}
 */
export const OrganizationRef_Document = (
  orgId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'organizations', orgId);
};

/**
 * Query for all organizations ordered by name
 */
export const OrganizationsOrderedRef_Query = (
  orderByField: string = 'name',
  orderDesc: boolean = false,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    OrganizationsRef_Collection(clientKey),
    orderBy(orderByField, orderDesc ? 'desc' : 'asc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/* ═══════════════════════════════════════════════════════════
   ORGANIZATION MEMBERSHIPS COLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to the organization memberships collection
 * Path: /clients/{clientKey}/organization_memberships
 */
export const OrganizationMembershipsRef_Collection = (
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'organization_memberships');
};

/**
 * Reference to a specific organization membership
 * Path: /clients/{clientKey}/organization_memberships/{membershipId}
 */
export const OrganizationMembershipRef_Document = (
  membershipId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'organization_memberships', membershipId);
};

/**
 * Query for memberships by user
 */
export const UserOrganizationMembershipsRef_Query = (
  userId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    OrganizationMembershipsRef_Collection(clientKey),
    where('user_id', '==', userId),
    orderBy('joined_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Query for memberships by organization
 */
export const OrganizationMembersRef_Query = (
  organizationId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    OrganizationMembershipsRef_Collection(clientKey),
    where('organization_id', '==', organizationId),
    orderBy('joined_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/* ═══════════════════════════════════════════════════════════
   ORGANIZATION INVITATIONS COLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to the organization invitations collection
 * Path: /clients/{clientKey}/organization_invitations
 */
export const OrganizationInvitationsRef_Collection = (
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  // Safety check - ensure clientKey is never null/undefined
  const safeClientKey = clientKey || 'athleto';
  return collection(db, 'clients', safeClientKey, 'organization_invitations');
};

/**
 * Reference to a specific organization invitation
 * Path: /clients/{clientKey}/organization_invitations/{invitationId}
 */
export const OrganizationInvitationRef_Document = (
  invitationId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'organization_invitations', invitationId);
};

/**
 * Query for invitations by organization
 */
export const OrganizationInvitationsRef_Query = (
  organizationId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    OrganizationInvitationsRef_Collection(clientKey),
    where('organization_id', '==', organizationId),
    orderBy('created_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Query for pending invitations by organization
 */
export const OrganizationPendingInvitationsRef_Query = (
  organizationId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    OrganizationInvitationsRef_Collection(clientKey),
    where('organization_id', '==', organizationId),
    where('status', '==', 'pending'),
    orderBy('created_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Query for invitation by code
 */
export const OrganizationInvitationByCodeRef_Query = (
  inviteCode: string,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  return query(
    OrganizationInvitationsRef_Collection(clientKey),
    where('invite_code', '==', inviteCode),
    limit(1)
  );
};
